import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { getEnvVars, createEnvVar, deleteEnvVar } from '../api/env';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';

export function EnvPage() {
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: vars = [] } = useQuery({ queryKey: ['env'], queryFn: getEnvVars });

  const createMutation = useMutation({
    mutationFn: () => createEnvVar(key, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['env'] });
      setKey(''); setValue(''); setError(null);
    },
    onError: () => setError('Failed to save variable.'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEnvVar,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['env'] }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Environment variables"
        description={
          <>
            Values are encrypted at rest and never returned by the API.
            Use <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{`{{env.KEY_NAME}}`}</code> in flow configs.
          </>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Add variable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Input
              placeholder="KEY_NAME"
              value={key}
              onChange={e => setKey(e.target.value)}
              className="flex-1"
            />
            <Input
              type="password"
              placeholder="Value"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!key.trim() || !value.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Saving…' : 'Add'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Added</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vars.map(v => (
            <TableRow key={v.id}>
              <TableCell className="font-mono font-medium">{v.key}</TableCell>
              <TableCell className="text-muted-foreground">{new Date(v.createdAt).toLocaleDateString()}</TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => { if (window.confirm(`Delete ${v.key}?`)) deleteMutation.mutate(v.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {vars.length === 0 && (
            <TableRow><TableCell colSpan={3} className="p-6 text-center text-muted-foreground">No variables</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
